// src/components/accueil/AccueilBE.tsx — SCE Issues
// Accueil d'une personne du BE (26/10) — PROVISOIRE, en attendant la validation
// de la maquette d'accueil BE du 20/10 (autour de leur avis : acceptation,
// localisation, causes). C'est l'ancienne page « Pour le BE » (/be, 24/10)
// devenue leur accueil : on ne la cherche plus dans le menu du compte.
//
//  - « Envoyés au BE Élec » : les problèmes transmis par « Envoyer au BE » à l'une
//    des personnes du BE du métier (lus dans SCE_Envois), « À vous » quand on en
//    était destinataire, qui l'a envoyé, quand, et son mot ;
//  - « Problèmes Élec en cours » : à qualifier ou en cours, le plus récent d'abord.
// Une personne de plusieurs BE choisit (ou Tous). L'administrateur la voit en se
// mettant à la place d'une personne du BE (« Voir en tant que »).
// Règles pures : utils/pageBE.

import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useChoixDeclaration } from '@/components/layout/ChoixDeclaration'
import { useOuvrirFiche } from '@/components/layout/ModaleRoute'
import { SuiteListe } from '@/components/layout/SuiteProblemes'
import { ListeProblemesTelephone } from '@/components/issues/ListeProblemesTelephone'
import { IssueRow, IssueRowHeader } from '@/components/issues/IssueRow'
import { ListeDense, SqueletteDense } from '@/components/ui/ListeDense'
import { EtatProbleme } from '@/components/ui/EtatProbleme'
import { RechercheGlobale } from './RechercheGlobale'
import { useEstBureau } from '@/hooks/useMediaQuery'
import { listerEnvoisProblemes } from '@/api/envoisBE.api'
import { getBE, metiersDuBE } from '@/config/be.config'
import { METIERS_LABELS_COURT } from '@/config/metiers.config'
import { lienProbleme } from '@/utils/liensFiches'
import { nomComplet } from '@/utils/noms'
import { envoisAuBE, jourMoisParis, problemesEnCours, type EnvoiRecu, type LigneEnvoi } from '@/utils/pageBE'
import type { Issue, Metier } from '@/types/issue.types'

export function AccueilBE({
  issues,
  loading,
  upn,
  bonjour,
}: {
  issues: Issue[]
  loading: boolean
  upn: string
  /** « Bonjour Adrien ». */
  bonjour: string
}): JSX.Element {
  const bureau = useEstBureau()
  const choixDeclaration = useChoixDeclaration()
  const siens = useMemo(() => metiersDuBE(upn), [upn])
  // De plusieurs BE : on choisit (ou tous).
  const [metier, setMetier] = useState<Metier | null>(null)
  const metiers: Metier[] = metier && siens.includes(metier) ? [metier] : [...siens]

  const [envois, setEnvois] = useState<LigneEnvoi[] | null>(null)
  const [erreurEnvois, setErreurEnvois] = useState<string | null>(null)
  useEffect(() => {
    let vivant = true
    listerEnvoisProblemes()
      .then((l) => vivant && setEnvois(l))
      .catch((err: unknown) => vivant && setErreurEnvois(err instanceof Error ? err.message : 'Envois illisibles.'))
    return () => {
      vivant = false
    }
  }, [])

  const cleMetiers = metiers.join(',')
  const recus = useMemo(() => {
    const be = metiers.flatMap((m) => getBE(m).map((p) => p.upn))
    return envois ? envoisAuBE(envois, issues, be, upn) : []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envois, issues, cleMetiers, upn])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const enCours = useMemo(() => problemesEnCours(issues, metiers), [issues, cleMetiers])

  const libelle = metiers.map((m) => METIERS_LABELS_COURT[m]).join(' · ')

  return (
    <section className="flex flex-col" style={{ gap: 20, padding: bureau ? '24px 40px 32px' : '14px 16px 24px', maxWidth: 1100, marginInline: 'auto', width: '100%', boxSizing: 'border-box' }}>
      {!bureau && <RechercheGlobale issues={issues} variante="page" />}
      <div className="flex flex-wrap items-end justify-between" style={{ gap: '12px 16px' }}>
        <div className="flex flex-col" style={{ gap: 2, minWidth: 0 }}>
          <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>{bonjour} · BE {libelle}</span>
          <h1 style={{ margin: 0, fontSize: bureau ? 29 : 25, fontWeight: 400, lineHeight: bureau ? '34px' : '30px' }}>Ce qui vous attend</h1>
        </div>
        <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
          {siens.length > 1 && (
            <div className="tableau-vues" role="group" aria-label="Métier" style={{ minWidth: 0, flex: 'none' }}>
              <button type="button" className="tableau-vue" aria-pressed={metier === null} onClick={() => setMetier(null)}>
                Tous
              </button>
              {siens.map((m) => (
                <button key={m} type="button" className="tableau-vue" aria-pressed={metiers.length === 1 && metiers[0] === m} onClick={() => setMetier(m)}>
                  {METIERS_LABELS_COURT[m]}
                </button>
              ))}
            </div>
          )}
          <Button variant="primary" size="lg" iconLeft={<Plus size={18} strokeWidth={2.5} />} onClick={choixDeclaration.basculer}>
            Déclarer
          </Button>
          {choixDeclaration.feuille}
        </div>
      </div>

      <Bloc titre={`Envoyés au BE ${libelle}`} nombre={envois ? recus.length : null}>
        {erreurEnvois && <Erreur texte={`Envois illisibles : ${erreurEnvois}`} />}
        {!erreurEnvois && (envois === null || (loading && issues.length === 0)) && <SqueletteDense lignes={3} />}
        {envois !== null && !loading && recus.length === 0 && <Vide texte="Aucun problème envoyé au BE pour l’instant (icône « Envoyer » d’une fiche problème)." />}
        {recus.length > 0 && (
          <SuiteListe libelle={`Envoyés au BE ${libelle}`} issues={recus.map((r) => r.issue)}>
            <div style={CARTE}>
              {recus.map((r) => (
                <LigneEnvoye key={r.issue.id} r={r} />
              ))}
            </div>
          </SuiteListe>
        )}
      </Bloc>

      <Bloc titre={`Problèmes ${libelle} en cours`} nombre={loading && issues.length === 0 ? null : enCours.length}>
        {loading && issues.length === 0 && <SqueletteDense lignes={5} />}
        {!loading && enCours.length === 0 && <Vide texte="Aucun problème en cours sur ce métier." />}
        {enCours.length > 0 &&
          (bureau ? (
            <SuiteListe libelle={`Problèmes ${libelle} en cours`} issues={enCours}>
              <ListeDense>
                <IssueRowHeader />
                {enCours.map((issue) => (
                  <IssueRow key={issue.id} issue={issue} />
                ))}
              </ListeDense>
            </SuiteListe>
          ) : (
            <ListeProblemesTelephone issues={enCours} />
          ))}
      </Bloc>
    </section>
  )
}

const CARTE = { backgroundColor: 'var(--color-white)', border: '1px solid var(--color-border)', borderRadius: 12, overflow: 'hidden' } as const

function Bloc({ titre, nombre, children }: { titre: string; nombre: number | null; children: ReactNode }): JSX.Element {
  return (
    <section className="flex flex-col" style={{ gap: 10 }}>
      <h2 style={{ margin: 0, fontSize: 18, lineHeight: '24px', fontWeight: 500 }}>
        {titre}
        {nombre !== null && <span style={{ marginLeft: 8, fontSize: 14, fontWeight: 400, color: 'var(--color-gray-70)' }}>{nombre}</span>}
      </h2>
      {children}
    </section>
  )
}

function Vide({ texte }: { texte: string }): JSX.Element {
  return <p style={{ ...CARTE, margin: 0, padding: '16px', fontSize: 15, lineHeight: '21px', color: 'var(--color-gray-70)' }}>{texte}</p>
}

function Erreur({ texte }: { texte: string }): JSX.Element {
  return <p style={{ ...CARTE, margin: 0, padding: '12px 16px', fontSize: 14, color: 'var(--color-error)' }}>{texte}</p>
}

/** Un problème envoyé : N° et équipement, titre, qui l'a envoyé et quand, son mot ; l'état à droite. */
function LigneEnvoye({ r }: { r: EnvoiRecu }): JSX.Element {
  const ouvrir = useOuvrirFiche()
  const dernier = r.envois[0]
  const qui = dernier ? nomComplet(dernier.expediteur, dernier.expediteur) : ''
  const envoi = dernier ? `Envoyé par ${qui} le ${jourMoisParis(dernier.le)}${r.envois.length > 1 ? ` · ${r.envois.length} envois` : ''}` : ''
  return (
    <button
      type="button"
      onClick={() => ouvrir(lienProbleme(r.issue.id))}
      className="flex items-start w-full"
      style={{ gap: 12, padding: '12px 16px', border: 'none', borderTop: '1px solid var(--color-gray-10)', background: 'var(--color-white)', textAlign: 'left', fontFamily: 'inherit', color: 'var(--color-black)', cursor: 'pointer' }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="flex flex-wrap items-center" style={{ gap: '2px 8px', fontSize: 13, lineHeight: '18px', color: 'var(--color-gray-70)', fontVariantNumeric: 'tabular-nums' }}>
          {r.issue.id}
          {r.issue.numeroSerie && <span>· {r.issue.numeroSerie}</span>}
          {r.aVous && (
            <span style={{ padding: '0 8px', borderRadius: 10, backgroundColor: 'var(--color-black)', color: 'var(--color-white)', fontSize: 12, lineHeight: '18px', fontWeight: 600 }}>À vous</span>
          )}
        </span>
        <span style={{ display: 'block', marginTop: 2, fontSize: 15, lineHeight: '21px', fontWeight: 500, overflowWrap: 'anywhere' }}>{r.issue.titre}</span>
        {envoi && <span style={{ display: 'block', marginTop: 2, fontSize: 13, lineHeight: '18px', color: 'var(--color-text-secondary)' }}>{envoi}</span>}
        {dernier?.note && (
          <span style={{ display: 'block', marginTop: 6, padding: '4px 10px', borderLeft: '3px solid var(--color-gray-20)', fontSize: 14, lineHeight: '20px', color: 'var(--color-gray-80)', overflowWrap: 'anywhere' }}>
            « {dernier.note} »
          </span>
        )}
      </span>
      <EtatProbleme statut={r.issue.statut} criticite={r.issue.criticite} />
    </button>
  )
}
